import { Box } from "@mui/material";
import type { FC, ReactNode } from "react";
import { BADGE, FONT_SIZE } from "../../../../../theme/tokens";
import type { SystemTabBadge } from "./utils";

type Props = {
	label: ReactNode;
	badge?: SystemTabBadge;
	/** Space between the label and its indicator, in theme spacing units. */
	gap: number;
};

/**
 * A tab's label with its optional trailing count pill / status dot — shared by the tab bar and its "More" menu, so a
 * tab reads the same in either place.
 */
export const SystemTabLabel: FC<Props> = ({ label, badge, gap }) => {
	if (!badge) return <>{label}</>;
	// DEFAULT: a badge without a colour is an alert — the error fill, as `SystemTabBadge` documents.
	const color = badge.color ?? "error";
	return (
		<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap }}>
			{label}
			<Box
				component="span"
				aria-label={badge.ariaLabel}
				role={badge.ariaLabel ? "status" : undefined}
				sx={(theme) =>
					badge.dot
						? { width: BADGE.DOT_SIZE, height: BADGE.DOT_SIZE, borderRadius: "50%", backgroundColor: theme.palette[color].main, flexShrink: 0 }
						: {
								display: "inline-flex",
								alignItems: "center",
								justifyContent: "center",
								minWidth: BADGE.PILL_SIZE,
								height: BADGE.PILL_SIZE,
								px: 0.5,
								borderRadius: BADGE.PILL_RADIUS,
								fontSize: FONT_SIZE.LABEL_XS,
								fontWeight: 600,
								lineHeight: 1,
								backgroundColor: theme.palette[color].main,
								color: theme.palette[color].contrastText,
							}
				}
			>
				{badge.dot ? null : badge.content}
			</Box>
		</Box>
	);
};
